import React, { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ContactForm = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const SUBJECT_OPTIONS = [
    { value: '', label: t('contact.form.subjects.placeholder') },
    { value: 'Opšte pitanje', label: t('contact.form.subjects.general') },
    { value: 'Tehnička podrška', label: t('contact.form.subjects.technical') },
    { value: 'Upravljanje nalogom', label: t('contact.form.subjects.account') },
    { value: 'Vaučeri i transakcije', label: t('contact.form.subjects.vouchers') },
    { value: 'Prijava problema', label: t('contact.form.subjects.problem') },
    { value: 'Predlog za poboljšanje', label: t('contact.form.subjects.suggestion') },
    { value: 'Ostalo', label: t('contact.form.subjects.other') },
  ];

  const validate = () => {
    const newErrors = {};
    if (!form?.name?.trim()) newErrors.name = t('contact.form.errors.nameRequired');
    if (!form?.email?.trim()) {
      newErrors.email = t('contact.form.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(form?.email)) {
      newErrors.email = t('contact.form.errors.emailInvalid');
    }
    if (!form?.subject) newErrors.subject = t('contact.form.errors.subjectRequired');
    if (!form?.message?.trim()) {
      newErrors.message = t('contact.form.errors.messageRequired');
    } else if (form?.message?.trim()?.length < 10) {
      newErrors.message = t('contact.form.errors.messageTooShort');
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      return;
    }

    const subject = encodeURIComponent(`[ValueBook] ${form?.subject}`);
    const body = encodeURIComponent(
      `Ime: ${form?.name}\nEmail: ${form?.email}\n\nPoruka:\n${form?.message}`
    );
    const mailtoLink = `mailto:support@valuebook.rs?subject=${subject}&body=${body}`;
    window.location.href = mailtoLink;
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center">
        <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-teal-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-3">{t('contact.form.successTitle')}</h3>
        <p className="text-gray-600 mb-6">{t('contact.form.successDesc')}</p>
        <button
          onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', message: '' }); }}
          className="text-teal-700 hover:text-teal-900 font-medium transition-colors"
        >
          {t('contact.form.sendAnother')}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.form.title')}</h2>
      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5" htmlFor="name">
            {t('contact.form.nameLabel')} <span className="text-red-500">*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            value={form?.name}
            onChange={handleChange}
            placeholder={t('contact.form.namePlaceholder')}
            className={`w-full px-4 py-3 border rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 transition-colors ${
              errors?.name ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
            }`}
          />
          {errors?.name && <p className="text-red-500 text-xs mt-1">{errors?.name}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5" htmlFor="email">
            {t('contact.form.emailLabel')} <span className="text-red-500">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            value={form?.email}
            onChange={handleChange}
            placeholder="vas@email.com"
            className={`w-full px-4 py-3 border rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 transition-colors ${
              errors?.email ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
            }`}
          />
          {errors?.email && <p className="text-red-500 text-xs mt-1">{errors?.email}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5" htmlFor="subject">
            {t('contact.form.subjectLabel')} <span className="text-red-500">*</span>
          </label>
          <select
            id="subject"
            name="subject"
            value={form?.subject}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 transition-colors ${
              errors?.subject ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
            }`}
          >
            {SUBJECT_OPTIONS?.map((opt) => (
              <option key={opt?.value} value={opt?.value} disabled={opt?.value === ''}>
                {opt?.label}
              </option>
            ))}
          </select>
          {errors?.subject && <p className="text-red-500 text-xs mt-1">{errors?.subject}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5" htmlFor="message">
            {t('contact.form.messageLabel')} <span className="text-red-500">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={form?.message}
            onChange={handleChange}
            rows={6}
            placeholder={t('contact.form.messagePlaceholder')}
            className={`w-full px-4 py-3 border rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 transition-colors resize-none ${
              errors?.message ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
            }`}
          />
          <div className="flex justify-between items-center mt-1">
            {errors?.message ? (
              <p className="text-red-500 text-xs">{errors?.message}</p>
            ) : (
              <span />
            )}
            <span className="text-xs text-gray-400">{form?.message?.length} {t('contact.form.characters')}</span>
          </div>
        </div>

        <button
          type="submit"
          className="w-full flex items-center justify-center gap-2 bg-teal-700 text-white font-bold py-3.5 px-6 rounded-lg hover:bg-teal-800 transition-colors"
        >
          <Send className="w-5 h-5" />
          {t('contact.form.submitBtn')}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
